import { useCallback, useEffect, useRef, useState } from 'react'
import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom'
import {
  Target,
  Calendar,
  Stethoscope,
  Phone,
  FileText,
  Pin,
  Boxes,
  FlaskConical,
  UserCog,
  Users,
  BarChart3,
  ChevronRight,
  ChevronDown,
  PhoneCall,
  Ticket,
  Scissors,
  PackagePlus,
  ArrowLeftRight,
  ShoppingCart,
  Package,
  BriefcaseMedical,
  Receipt,
  Tag,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Menu as MenuIcon,
  X as XIcon,
  CalendarRange,
} from 'lucide-react'
import { useMe, useDisplayName } from '../lib/me.js'
import { apiGet } from '../lib/api.js'
import { useXanoRealtime } from '../lib/realtime.js'
import { canManage, canAccessStaff, canViewPromotion, isLabOnly, isAgencyOnly } from '../lib/role.js'
import { nameInitial } from '../lib/text.js'
import { useNotice } from '../lib/notice.js'
import { useSchedulePending, schedulePendingLoaded } from '../lib/schedulePending.js'
import { clearAllRecentPatients } from '../lib/recentPatients.js'
import BrandMark from './BrandMark.jsx'
import SearchTrigger from './SearchTrigger.jsx'
import ChatInboxTrigger from './ChatInboxTrigger.jsx'
import ChatToaster from './ChatToaster.jsx'
import ChatDockHost from './ChatDockHost.jsx'

const NAV = [
  { to: '/appointments', label: 'นัดหมาย',   icon: Calendar },
  { to: '/treatments',   label: 'การรักษา',  icon: Stethoscope },
  { to: '/huddle',       label: 'Daily Goal', icon: Target },
  { to: '/calls',        label: 'Call',      icon: Phone, badge: 'calls' },
  { to: '/reports',      label: 'รายงาน',    icon: FileText },
  { to: '/follow-ups',   label: 'ติดตาม',    icon: Pin, children: [
    { to: '/follow-ups/recall', label: 'Recall', icon: PhoneCall },
    { to: '/follow-ups/ticket', label: 'Ticket', icon: Ticket },
  ] },
  { to: '/lab',          label: 'Lab',       icon: FlaskConical },
  { to: '/stock',        label: 'Stock',     icon: Boxes, children: [
    { to: '/stock/cut',      label: 'ตัด Stock', icon: Scissors },
    { to: '/stock/transfer', label: 'โอนย้าย',   icon: ArrowLeftRight },
    { to: '/purchase',       label: 'จัดซื้อ',    icon: ShoppingCart },
    { to: '/stock/items',    label: 'คลังของ',   icon: PackagePlus },
    { to: '/manager/assets', label: 'สินทรัพย์',  icon: Package, managerOnly: true },
  ] },
  // Manager section — visible to managers OR staff (HR is not a "manager" but
  // reaches the พนักงาน child). Children are individually role-gated.
  // `badge: 'schedule'` appears TWICE on purpose — on the child that owns the
  // queue and on the parent as a roll-up. The Manager section is an accordion and
  // is closed on every page outside it, so a badge only on the child would be
  // invisible exactly when it is needed. See the render below.
  { to: '/manager', label: 'Manager', icon: UserCog, badge: 'schedule', show: ({ manager, staff }) => manager || staff, children: [
    { to: '/manager/schedule', label: 'ตารางเวร', icon: CalendarRange, staffOnly: true, badge: 'schedule' },
    { to: '/manager/employee', label: 'พนักงาน', icon: Users, staffOnly: true },
    { to: '/manager/dentist',  label: 'ทันตแพทย์', icon: BriefcaseMedical, managerOnly: true },
    { to: '/manager/promotion', label: 'โปรโมชั่น', icon: Tag, promotionOnly: true },
    { to: '/manager/expense',  label: 'ค่าใช้จ่าย', icon: Receipt, managerOnly: true },
  ] },
  { to: '/statistic',    label: 'Statistic', icon: BarChart3 },
]

/* ── missed-call badge ────────────────────────────────────────────────────────
 * 🔑 THIS POLL TOOK THE CRM DOWN ON 2026-08-24. Read before touching it.
 *
 * It used to call `/api/phone?call_result=missed&done=false&per_page=500` — 500
 * COMPLETE call records plus four addon subqueries per row — and throw all of it
 * away to compute one integer. On every page, for every non-lab user, every 60s,
 * on every tab focus, and on every `call_update` broadcast. The Worker measured
 * 49ms CPU average / 105ms peak on that route against 2ms for everything else,
 * and threw `Worker exceeded CPU time limit` — the Error 1102 the whole app was
 * serving, because Pages Functions share one Worker and a request that blows the
 * CPU ceiling takes unrelated page loads down with it.
 *
 * ⚠️ CORRECTION, same day: the 500 was a CAP, not a payload. Measured in Xano —
 * that query matched only THIRTEEN rows. So response size cannot have caused the
 * CPU, and the leading suspect is now a per-request `Intl.DateTimeFormat` inside
 * functions/api/phone.js (since hoisted to module scope). The fetch was still
 * wasteful and still amplified by every tab, but do not repeat the payload story
 * as if it were established.
 *
 * Three things keep it from coming back, and all three matter:
 *   1. The count is computed in the DATABASE now — `/api/phone/new-missed-count`
 *      returns `{ count }`, Xano api 634 does the grouping. Never re-introduce a
 *      list fetch here for a number.
 *   2. That route is edge-cached ~30s on a SHARED key (the badge is a clinic-wide
 *      queue depth, not a per-user inbox), so N tabs collapse to one upstream read.
 *   3. The timers are jittered. The old realtime debounce was 600ms flat and
 *      per-tab, so it never coalesced ACROSS tabs: one incoming call made every
 *      open tab in the clinic fetch at the same instant. Jitter is what turns a
 *      thundering herd into a trickle — keep it.
 *
 * `enabled` is false for roles with no Call page (a Lab technician), so the poll
 * doesn't 403 against their API allowlist for a badge they never see.
 */
// The poll is a SAFETY NET, not the update mechanism. `call_update` already fires
// on both halves of the story — a new call arriving (Xano table trigger 15,
// `phone_add patient`) and every claim / release / done / note (api 467 broadcasts
// it on every PATCH). So realtime coverage is complete and the timer only exists
// to heal a dropped socket. 5 minutes, not 60 seconds.
const BADGE_POLL_MS = 300000
const BADGE_POLL_JITTER_MS = 60000
const BADGE_RT_DEBOUNCE_MS = 2000
const BADGE_RT_JITTER_MS = 4000
const badgePollDelay = () => BADGE_POLL_MS + Math.floor(Math.random() * BADGE_POLL_JITTER_MS)
const badgeRtDelay = () => BADGE_RT_DEBOUNCE_MS + Math.floor(Math.random() * BADGE_RT_JITTER_MS)

function useNewMissedCount(enabled = true) {
  const [count, setCount] = useState(null)
  const load = useCallback(async () => {
    if (!enabled) return
    try {
      const r = await apiGet('/api/phone/new-missed-count')
      const n = Number(r?.count)
      if (Number.isFinite(n)) setCount(n)
    } catch (e) {
      // Keep the previous value — but SAY SO. A bare `catch {}` here is what hid a
      // 400 on every poll for two days (Xano routed the old slashed path to
      // GET /phone/{phone_id}); the badge just stayed blank and looked like an
      // empty queue. A blank badge and a broken badge must not look identical.
      console.warn('[missed-badge] count fetch failed:', e?.message || e)
    }
  }, [enabled])
  // Load on mount + realtime, and SELF-HEAL on a jittered ~60-80s timer and on
  // tab focus so the badge can't drift from the Call page even if a broadcast is
  // missed. Recursive setTimeout rather than setInterval: a fixed interval
  // re-synchronises every tab onto the same tick, which is the herd again.
  useEffect(() => {
    if (!enabled) return undefined
    let alive = true
    let id = null
    let focusTimer = null
    const tick = async () => {
      await load()
      if (alive) id = setTimeout(tick, badgePollDelay())
    }
    tick()
    // Jittered like the realtime path, and for the same reason. A bare load()
    // here is a herd with NO spread at all: when a huddle ends, every tab in the
    // clinic regains focus inside the same second and fires at once. The badge
    // is a queue depth — a few seconds late on tab focus is imperceptible, and
    // the server now single-flights besides.
    const onFocus = () => {
      if (document.visibilityState === 'hidden') return
      clearTimeout(focusTimer)
      focusTimer = setTimeout(load, Math.floor(Math.random() * BADGE_RT_JITTER_MS))
    }
    document.addEventListener('visibilitychange', onFocus)
    window.addEventListener('focus', onFocus)
    return () => {
      alive = false
      clearTimeout(id)
      clearTimeout(focusTimer)
      document.removeEventListener('visibilitychange', onFocus)
      window.removeEventListener('focus', onFocus)
    }
  }, [load])
  const timer = useRef(null)
  useXanoRealtime('call_update', () => {
    clearTimeout(timer.current)
    timer.current = setTimeout(load, badgeRtDelay())
  })
  useEffect(() => () => clearTimeout(timer.current), [])
  return count
}

export default function Layout() {
  // 🔑 THE ONE autoLoad IN THE APP. The sidebar is always mounted, so it owns the
  // fetch and the window-focus refresh; every other consumer (MeLayout's tab badges)
  // subscribes read-only. Two autoLoads would mean two identical requests on every
  // tab switch, which is the exact mistake chat.js documents.
  const notice = useNotice({ autoLoad: true })
  const me = useMe()
  // The sidebar card names the person the way the clinic does — the employee
  // record's Thai ชื่อ-นามสกุล, falling back to the account label (lib/me.js).
  const displayName = useDisplayName()
  const manager = canManage(me)
  const staff = canAccessStaff(me)
  // Narrower than `manager`: Stock Manager reaches the Manager area but not the
  // promotion catalog (lib/role.js#canViewPromotion).
  const promo = canViewPromotion(me)
  // A Lab-only account (functional role = just "Lab") sees ONLY the Lab menu.
  const labOnly = isLabOnly(me)
  // An agency account sees ONLY the Statistic + Call menus.
  const agencyOnly = isAgencyOnly(me)
  const nav = labOnly
    ? NAV.filter((n) => n.to === '/lab')
    : agencyOnly
    ? NAV.filter((n) => n.to === '/statistic' || n.to === '/calls')
    : NAV.filter((n) => (n.show ? n.show({ manager, staff, promo }) : (!n.managerOnly || manager) && (!n.staffOnly || staff) && (!n.promotionOnly || promo)))
  const newMissed = useNewMissedCount(!labOnly)
  // ตารางเวร queue depth — คำขอลา waiting on a decision plus ขอเวร that already has
  // interest waiting on a pick. 🔑 `staff` (Manager / GM / HR / Admin) is the SAME
  // set the middleware admits to /api/hr/*, deliberately NOT `manager`: a Stock
  // Manager reaches the Manager section but not this page, and would be 403'd
  // fetching a badge they are never shown. THE SIDEBAR IS THE ONE autoLoad — the
  // tab pills inside ตารางเวร › ทันตแพทย์ subscribe to the same store read-only.
  const schedule = useSchedulePending({ autoLoad: true, enabled: staff })
  // null until the first answer arrives, so the badge is ABSENT rather than a
  // confident 0. A blank badge and a broken badge must not look identical.
  const scheduleTotal = staff && schedulePendingLoaded() ? schedule.total : null
  // What the number is made of. A badge nobody can decompose is a badge people
  // learn to ignore — and these are two different queues that happen to share a page.
  const scheduleHint = (() => {
    const parts = []
    if (schedule.leave > 0) parts.push(`คำขอลา ${schedule.leave}`)
    if (schedule.shift > 0) parts.push(`ขอเวร ${schedule.shift}`)
    return parts.length ? `รอดำเนินการ — ${parts.join(' · ')}` : ''
  })()
  // `badge` on a NAV row is a KEY, not a number: the two counters come from two
  // different stores and neither belongs in a module-level table.
  const badgeFor = (key) => {
    if (key === 'calls') return newMissed
    if (key === 'schedule') return scheduleTotal
    return null
  }
  const badgeHint = (key) => (key === 'schedule' ? scheduleHint : '')
  // 🎨 Two colours, and the difference carries meaning rather than decoration.
  // ROSE is the Call badge: someone is on the phone now. AMBER is paperwork
  // waiting on you — and it is the same amber the ขอเวร / การลา tab pills use on
  // the page this badge opens, so the badge and its destination visibly agree.
  const badgeTone = (key) => (key === 'schedule'
    ? 'bg-amber-400 text-brand-900'
    : 'bg-rose-500 text-white')
  const dotTone = (key) => (key === 'schedule' ? 'bg-amber-400' : 'bg-rose-400')
  const { pathname } = useLocation()
  // Accordion: at most one section expanded at a time. Selecting any other
  // main menu (with or without children) collapses the previous one.
  const [openKey, setOpenKey] = useState(null)
  const toggleSection = (to) => setOpenKey((k) => (k === to ? null : to))
  // Follow the route: landing inside a section opens it (and closes the rest).
  useEffect(() => {
    const section = nav.find((n) => n.children && (
      pathname === n.to || pathname.startsWith(n.to + '/') ||
      n.children.some((c) => pathname === c.to || pathname.startsWith(c.to + '/'))
    ))
    setOpenKey(section ? section.to : null)
  }, [pathname]) // eslint-disable-line react-hooks/exhaustive-deps
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('ss_sidebar_collapsed') === '1' } catch { return false }
  })
  useEffect(() => {
    try { localStorage.setItem('ss_sidebar_collapsed', collapsed ? '1' : '0') } catch { /* ignore */ }
  }, [collapsed])
  // Phones and tablets (< lg, 1024px): the sidebar becomes an off-canvas menu
  // behind ☰ in a slim top bar (decision #5, 2026-10-18 — mirrors PatientLayout).
  // It always opens EXPANDED there; the collapsed rail is a desktop preference,
  // so `rail` (not `collapsed`) drives the markup. Closes on navigation, on a
  // backdrop tap and on Esc.
  const [isDesktop, setIsDesktop] = useState(() => typeof window === 'undefined' || window.matchMedia('(min-width: 1024px)').matches)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const on = () => setIsDesktop(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  const [mobileOpen, setMobileOpen] = useState(false)
  useEffect(() => { setMobileOpen(false) }, [pathname])
  useEffect(() => {
    if (!mobileOpen) return undefined
    const onKey = (e) => { if (e.key === 'Escape') setMobileOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mobileOpen])
  const rail = collapsed && isDesktop
  // A Lab-only account is confined to /lab plus the self-service area (enforced
  // server-side too, by LAB_ONLY_PAGES) — keep client-side navigation from ever
  // landing it on another page in this (employee) layout. A bench technician still
  // needs their own clock-in, schedule and settings.
  const labAllowed = pathname === '/lab' || pathname === '/me' || pathname.startsWith('/me/')
  if (labOnly && !labAllowed) return <Navigate to="/lab" replace />
  // An agency account is confined to /statistic + /calls (enforced server-side too).
  if (agencyOnly && pathname !== '/statistic' && pathname !== '/calls') return <Navigate to="/statistic" replace />
  return (
    <div className="flex flex-col lg:flex-row h-full min-h-screen">
      {/* Phones / tablets: slim top bar — ☰ opens the menu, search stays one tap away. */}
      <div className="lg:hidden flex-none h-14 flex items-center gap-2.5 px-3 bg-brand-900 text-white">
        <button type="button" onClick={() => setMobileOpen(true)} aria-label="เปิดเมนู" aria-expanded={mobileOpen}
          className="w-10 h-10 -ml-1 rounded-lg flex items-center justify-center hover:bg-white/10">
          <MenuIcon size={20} />
        </button>
        <div className="flex-none rounded-full bg-white p-0.5"><BrandMark size={26} /></div>
        <div className="flex-1 min-w-0 text-[15px] font-semibold truncate">Smile Seasons</div>
        {!labOnly && !agencyOnly && <div className="flex-none w-11"><SearchTrigger collapsed tone="dark" /></div>}
      </div>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-[2px]" onClick={() => setMobileOpen(false)} aria-hidden />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-[264px] max-w-[85vw] transition-transform duration-200 ${mobileOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}
          lg:static lg:z-auto lg:max-w-none lg:translate-x-0 lg:shadow-none lg:transition-[width] ${rail ? 'lg:w-[68px]' : 'lg:w-[232px]'}
          flex-none flex flex-col bg-brand-900 border-r border-black/10`}
      >
        {/* Brand */}
        <div className="px-4 pt-5 pb-4 flex items-center gap-2.5">
          <div className="flex-none rounded-full bg-white p-0.5 ring-1 ring-white/20">
            <BrandMark size={32} />
          </div>
          {!rail && (
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-semibold text-white leading-tight">Smile Seasons</div>
              <div className="text-[12px] uppercase tracking-[0.14em] text-brand-200/70 mt-0.5">Clinic CRM</div>
            </div>
          )}
          <button type="button" onClick={() => setMobileOpen(false)} aria-label="ปิดเมนู"
            className="lg:hidden -mr-1 w-9 h-9 rounded-lg flex items-center justify-center text-brand-200/80 hover:text-white hover:bg-white/10">
            <XIcon size={18} />
          </button>
        </div>

        {/* Pinned patient-search trigger — hidden for Lab-only / agency (no patient access). */}
        {!labOnly && !agencyOnly && (
          <div className="px-3 pb-3">
            <SearchTrigger collapsed={rail} tone="dark" />
          </div>
        )}

        {/* ข้อความ — unread patient threads. Sits directly under search because
            it is the same move: find a patient, open them in a tab. Hidden for
            Lab-only / agency for the same reason search is: no patient access.
            Not a route — see ChatInboxTrigger. */}
        {!labOnly && !agencyOnly && (
          <div className="px-2 pb-2">
            <ChatInboxTrigger collapsed={rail} tone="dark" />
          </div>
        )}

        {!rail && (
          <div className="px-4 pb-2 text-[12px] font-medium uppercase tracking-[0.12em] text-brand-200/70">
            เมนูหลัก
          </div>
        )}

        <nav className="flex-1 px-2 space-y-0.5 overflow-y-auto scrollbar-on-dark">
          {nav.map((item) => {
            const { to, label, icon: Icon, badge, hasMore, children } = item
            const count = badgeFor(badge)
            const hint = badgeHint(badge)

            // Parent with a real submenu — expandable inline (only when the
            // sidebar is open; collapsed falls through to the plain icon link).
            if (children && !rail) {
              const open = openKey === to
              return (
                <div key={to}>
                  <NavLink
                    to={to}
                    end
                    onClick={() => setOpenKey(to)}
                    className={({ isActive }) =>
                      `group relative flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[14.5px] font-medium transition-colors ${
                        isActive ? 'bg-white/15 text-white' : 'text-brand-100/85 hover:bg-white/10 hover:text-white'
                      }`
                    }
                  >
                    <Icon size={17} className="flex-none group-hover:text-white" strokeWidth={1.75} />
                    <span className="flex-1 truncate">{label}</span>
                    {/* 🔑 THE ROLL-UP, and it is the reason this feature is not just
                        a badge on the child. This section is an accordion: it is
                        CLOSED on every page outside /manager, which is where a
                        manager spends the day. A number that only appears once you
                        have already opened the section tells you nothing you did
                        not just learn by opening it. Hidden while OPEN — the child
                        row carries it then, and the same number twice in one column
                        reads as two different things. */}
                    {!open && count != null && count > 0 && (
                      <span className={`flex-none inline-flex items-center justify-center h-[18px] min-w-[18px] px-1.5 rounded-full text-[11px] font-semibold leading-none tabular-nums ${badgeTone(badge)}`} title={hint || undefined}>
                        {count > 99 ? '99+' : count}
                      </span>
                    )}
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSection(to) }}
                      className="-mr-1 p-0.5 text-brand-200/60 hover:text-white"
                      aria-label={open ? 'ย่อเมนูย่อย' : 'ขยายเมนูย่อย'}
                    >
                      <ChevronDown size={14} className={`transition-transform ${open ? '' : '-rotate-90'}`} strokeWidth={2} />
                    </button>
                  </NavLink>
                  {open && (
                    <div className="mt-0.5 mb-1 ml-[22px] pl-3 border-l border-white/15 space-y-0.5">
                      {children.filter((c) => (!c.managerOnly || manager) && (!c.staffOnly || staff) && (!c.promotionOnly || promo)).map(({ to: cto, label: clabel, icon: CIcon, badge: cbadge }) => {
                        const ccount = badgeFor(cbadge)
                        const chint = badgeHint(cbadge)
                        return (
                        <NavLink
                          key={cto}
                          to={cto}
                          title={ccount > 0 ? (chint || undefined) : undefined}
                          className={({ isActive }) =>
                            `group flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[13.5px] font-medium transition-colors ${
                              isActive ? 'bg-white/15 text-white' : 'text-brand-100/70 hover:bg-white/10 hover:text-white'
                            }`
                          }
                        >
                          <CIcon size={15} className="flex-none group-hover:text-white" strokeWidth={1.75} />
                          <span className="flex-1 truncate">{clabel}</span>
                          {ccount != null && ccount > 0 && (
                            <span className={`flex-none inline-flex items-center justify-center h-[18px] min-w-[18px] px-1.5 rounded-full text-[11px] font-semibold leading-none tabular-nums ${badgeTone(cbadge)}`}>
                              {ccount > 99 ? '99+' : ccount}
                            </span>
                          )}
                        </NavLink>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            }

            return (
            <NavLink
              key={to}
              to={to}
              title={rail ? (count > 0 && hint ? `${label} — ${hint}` : label) : undefined}
              onClick={() => setOpenKey(null)}
              className={({ isActive }) =>
                `group relative flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[14.5px] font-medium transition-colors ${
                  isActive
                    ? 'bg-white/15 text-white'
                    : 'text-brand-100/85 hover:bg-white/10 hover:text-white'
                } ${rail ? 'justify-center' : ''}`
              }
            >
              <Icon size={17} className={`flex-none ${'group-hover:text-white'}`} strokeWidth={1.75} />
              {/* Collapsed rail: a bare dot, because there is no room for digits.
                  Both the missed-call queue and the ตารางเวร queue need one —
                  a badge only the expanded sidebar shows is invisible to everyone
                  who works collapsed, which is the same argument the profile block
                  below already makes. The count itself lives in the tooltip. */}
              {rail && count != null && count > 0 && (
                <span className={`absolute top-1 right-1 h-2 w-2 rounded-full ring-2 ring-brand-900 ${dotTone(badge)}`} />
              )}
              {!rail && (
                <>
                  <span className="flex-1 truncate">{label}</span>
                  {/* The pill is a FLEX BOX, not a padded inline span. `py-0.5 +
                      leading-none` centred the digits by luck of the font metrics and
                      sat them visibly high; a fixed 18px box with items-center is the
                      only way both "9" and "99+" ride on the same optical centre. */}
                  {count != null && count > 0 && (
                    <span className={`flex-none inline-flex items-center justify-center h-[18px] min-w-[18px] px-1.5 rounded-full text-[11px] font-semibold leading-none tabular-nums ${badgeTone(badge)}`}>
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                  {(hasMore || children) && <ChevronRight size={13} className="text-brand-200/50" strokeWidth={2} />}
                </>
              )}
            </NavLink>
            )
          })}
        </nav>

        {/* Profile + collapse */}
        <div className="px-2 pb-3 pt-2 border-t border-white/10">
          {/* The profile block IS the door to พื้นที่ของฉัน (/me) — clock-in,
              shift schedule, salary and settings. The logout icon stays a
              separate control beside it, so "open my area" and "leave" are two
              distinct targets rather than one ambiguous row. */}
          {!rail && me && (
            <div className="px-1 py-1 rounded-lg flex items-center gap-1">
              <NavLink
                to="/me"
                onClick={() => setOpenKey(null)}
                className={({ isActive }) =>
                  `flex-1 min-w-0 px-1.5 py-1.5 rounded-lg flex items-center gap-2.5 transition-colors ${
                    isActive ? 'bg-white/15' : 'hover:bg-white/10'
                  }`
                }
                title="พื้นที่ของฉัน"
              >
                {({ isActive }) => (
                  <>
                    <div className="relative flex-none">
                      <div className="w-8 h-8 rounded-full bg-white/15 text-white flex items-center justify-center overflow-hidden ring-1 ring-white/20">
                        {me.image
                          ? <img src={me.image} alt="" className="w-full h-full object-cover" />
                          : <span className="text-[14px] font-semibold">{nameInitial(displayName || me.email)}</span>}
                      </div>
                      {/* หนังสือเตือน that need acknowledging, decided leave, an
                          unanswered off-boarding questionnaire. The COUNT sits on the
                          expanded block because there is room for it; the collapsed
                          rail gets a bare dot. Both branches need one — a dot on only
                          the expanded sidebar is invisible to everyone who works
                          collapsed, which is half the staff. */}
                      {notice.total > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold leading-[16px] text-center ring-2 ring-brand-900">
                          {notice.total > 9 ? '9+' : notice.total}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[14px] font-semibold truncate text-white">{displayName || '—'}</div>
                      <div className="text-[12.5px] text-brand-200/70 truncate">{me.email}</div>
                    </div>
                  </>
                )}
              </NavLink>
              <a
                href="/__app/logout"
                title="ออกจากระบบ"
                onClick={() => clearAllRecentPatients()}
                className="p-1.5 rounded-md text-brand-200/70 hover:text-white hover:bg-rose-500/80 transition-colors flex-none"
              >
                <LogOut size={14} strokeWidth={2} />
              </a>
            </div>
          )}
          {/* Collapsed rail: the avatar alone still has to reach /me — and it needs
              the dot too. A notification only the expanded sidebar shows is invisible
              to everyone who works collapsed. */}
          {rail && me && (
            <div className="relative mx-auto mb-1 w-9 h-9">
              <NavLink
                to="/me"
                onClick={() => setOpenKey(null)}
                title={notice.total > 0 ? `พื้นที่ของฉัน — มี ${notice.total} เรื่องที่ยังไม่ได้อ่าน` : 'พื้นที่ของฉัน'}
                className={({ isActive }) =>
                  `w-9 h-9 rounded-full flex items-center justify-center overflow-hidden ring-1 transition-colors ${
                    isActive ? 'ring-white/60 bg-white/20' : 'ring-white/20 bg-white/10 hover:ring-white/50'
                  }`
                }
              >
                {me.image
                  ? <img src={me.image} alt="" className="w-full h-full object-cover" />
                  : <span className="text-[14px] font-semibold text-white">{nameInitial(displayName || me.email)}</span>}
              </NavLink>
              {notice.total > 0 && (
                <span className="pointer-events-none absolute -top-0.5 -right-0.5 w-[10px] h-[10px] rounded-full bg-rose-500 ring-2 ring-brand-900" />
              )}
            </div>
          )}
          <button
            onClick={() => setCollapsed((v) => !v)}
            className="mt-1 w-full hidden lg:flex items-center justify-center gap-1.5 text-[12.5px] text-brand-200/70 hover:text-white py-1.5 rounded-md hover:bg-white/10 transition-colors"
            title={rail ? 'ขยาย' : 'ย่อ'}
          >
            {rail ? <PanelLeftOpen size={14} /> : <><PanelLeftClose size={14} /><span>ย่อแถบเมนู</span></>}
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 min-h-0 overflow-auto">
        <Outlet />
      </main>

      {/* A message about a patient still has to find you when you are on the
          schedule, the ledger, or anywhere else. Mounted in both layouts;
          only one of them is ever rendered. */}
      {!labOnly && !agencyOnly && <ChatToaster />}
      {/* Answers the toast / inbox click with a dock over this page, not a new tab. */}
      {!labOnly && !agencyOnly && <ChatDockHost />}
    </div>
  )
}
